import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ApiError, getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/useToast';

import {
  useReplaceTenantRolePermissions,
  useTenantRolePermissions,
  type PermissionVocabularyViewModel,
} from '../api';
import { formatPermissionLabel } from '../permissionFormat';
import { PermissionGrammarBuilder } from './PermissionGrammarBuilder';

/** Sorted-set equality, so re-ordering doesn't read as a change. */
function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
}

/**
 * The two-pane grammar-permission editor for a role (PTV2-1444), inline on the role page. Left: the
 * enum-driven builder (compose + add); right: the currently-assigned permissions as a vertical table —
 * a human "value" per row plus its full grammar string — with a filter and per-row remove. Saves via
 * replace-all `PUT /…/roles/{guid}/permissions`; the server's escalation-guard (403) / validation (400)
 * rejection surfaces inline.
 */
export function RolePermissionsEditor({
  tenantUuid,
  roleGuid,
  vocabulary,
}: {
  readonly tenantUuid: string;
  readonly roleGuid: string;
  readonly vocabulary: PermissionVocabularyViewModel;
}): ReactElement {
  const { t } = useTranslation();
  const permissionsQuery = useTenantRolePermissions(tenantUuid, roleGuid, true);
  const replace = useReplaceTenantRolePermissions();

  const [assigned, setAssigned] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const initedRef = useRef<string | null>(null);

  // Initialize the working set once per role when the fetched permissions first arrive.
  useEffect(() => {
    if (permissionsQuery.isSuccess && initedRef.current !== roleGuid) {
      setAssigned([...permissionsQuery.data]);
      initedRef.current = roleGuid;
    }
  }, [roleGuid, permissionsQuery.isSuccess, permissionsQuery.data]);

  const original = permissionsQuery.data ?? [];
  const dirty = !sameSet(assigned, original);

  const filteredAssigned = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle.length === 0) {
      return assigned;
    }
    return assigned.filter(
      (permission) =>
        permission.toLowerCase().includes(needle) ||
        formatPermissionLabel(permission, vocabulary).toLowerCase().includes(needle),
    );
  }, [assigned, filter, vocabulary]);

  function addPermission(permission: string): void {
    setAssigned((prev) => (prev.includes(permission) ? prev : [...prev, permission]));
  }

  function removePermission(permission: string): void {
    setAssigned((prev) => prev.filter((p) => p !== permission));
  }

  function handleSave(): void {
    replace.mutate(
      { tenantUuid, guid: roleGuid, permissions: assigned },
      {
        onSuccess: (stored) => {
          setAssigned(stored);
          toast({ title: t('routes.platform.detail.roles.permissions.saved'), variant: 'success' });
        },
      },
    );
  }

  const isForbidden = replace.error instanceof ApiError && replace.error.status === 403;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">
            {t('routes.platform.detail.roles.permissions.addHeading')}
          </p>
          <PermissionGrammarBuilder
            vocabulary={vocabulary}
            onAdd={addPermission}
            disabled={replace.isPending}
          />
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            {t('routes.platform.detail.roles.permissions.assignedHeading', { count: assigned.length })}
          </p>
          <Input
            value={filter}
            onChange={(e) => { setFilter(e.target.value); }}
            placeholder={t('routes.platform.detail.roles.permissions.filterPlaceholder')}
            aria-label={t('routes.platform.detail.roles.permissions.filterPlaceholder')}
            autoComplete="off"
            spellCheck={false}
          />
          {permissionsQuery.isPending ? (
            <Skeleton className="h-24 w-full rounded-md" aria-hidden="true" />
          ) : (
            <div className="max-h-80 overflow-y-auto rounded-md border border-border">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-muted/40">
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2 font-medium">
                      {t('routes.platform.detail.roles.permissions.assignedTable.value')}
                    </th>
                    <th className="px-3 py-2 font-medium">
                      {t('routes.platform.detail.roles.permissions.assignedTable.grammar')}
                    </th>
                    <th className="w-8 px-2 py-2">
                      <span className="sr-only">
                        {t('routes.platform.detail.roles.permissions.assignedTable.actions')}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssigned.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-3 py-3 text-sm text-muted-foreground">
                        {t('routes.platform.detail.roles.permissions.assignedEmpty')}
                      </td>
                    </tr>
                  ) : (
                    filteredAssigned.map((permission) => (
                      <tr key={permission} className="border-t border-border">
                        <td className="px-3 py-2 text-foreground">
                          {formatPermissionLabel(permission, vocabulary)}
                        </td>
                        <td className="px-3 py-2">
                          <code className="font-mono text-xs text-muted-foreground">{permission}</code>
                        </td>
                        <td className="px-2 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => { removePermission(permission); }}
                            aria-label={t('routes.platform.detail.roles.permissions.removeAction', {
                              permission: formatPermissionLabel(permission, vocabulary),
                            })}
                            className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-destructive"
                          >
                            <X className="h-3.5 w-3.5" aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {replace.error !== null && (
        <p role="alert" className="text-sm text-red-600">
          {isForbidden
            ? t('routes.platform.detail.roles.permissions.errors.forbidden')
            : t('routes.platform.detail.roles.permissions.errors.submit', {
                code: getErrorCodeForUi(replace.error),
              })}
        </p>
      )}

      <div className="flex justify-end">
        <Button type="button" onClick={handleSave} loading={replace.isPending} disabled={!dirty}>
          {t('routes.platform.detail.roles.permissions.save')}
        </Button>
      </div>
    </div>
  );
}
